import { useCallback, useEffect, useState } from 'react';
import { liveMatchService } from '../../../api/service.ts';
import type { FieldPosition, GameStateSnapshot, LiveGameEvent, LivePlayer } from '../liveMatchTypes.ts';

function rotateLineup(lineup: FieldPosition[]): FieldPosition[] {
  const rotation: Record<number, number> = { 1: 6, 2: 1, 3: 2, 4: 3, 5: 4, 6: 5 };
  return lineup.map((fp) => ({ ...fp, position: rotation[fp.position] }));
}

function applyEventToSnapshot(event: LiveGameEvent, snap: GameStateSnapshot): GameStateSnapshot {
  switch (event.type) {
    case 'POINT_HOME': {
      const sideOut = snap.servingTeamNow !== 'HOME';
      return {
        ...snap,
        homePoints: event.homePoints,
        awayPoints: event.awayPoints,
        servingTeamNow: 'HOME',
        homeLineup: sideOut ? rotateLineup(snap.homeLineup) : snap.homeLineup,
      };
    }
    case 'POINT_AWAY': {
      const sideOut = snap.servingTeamNow !== 'AWAY';
      return {
        ...snap,
        homePoints: event.homePoints,
        awayPoints: event.awayPoints,
        servingTeamNow: 'AWAY',
        awayLineup: sideOut ? rotateLineup(snap.awayLineup) : snap.awayLineup,
      };
    }
    case 'TIMEOUT_HOME':
      return { ...snap, homeTimeouts: snap.homeTimeouts + 1 };
    case 'TIMEOUT_AWAY':
      return { ...snap, awayTimeouts: snap.awayTimeouts + 1 };
    case 'SUBSTITUTION_HOME': {
      if (event.playerId == null || event.substituteId == null) return snap;
      return {
        ...snap,
        homeLineup: snap.homeLineup.map((fp) =>
          fp.playerId === event.playerId ? { ...fp, playerId: event.substituteId! } : fp,
        ),
        homeBench: snap.homeBench.map((id) => (id === event.substituteId ? event.playerId! : id)),
      };
    }
    case 'SUBSTITUTION_AWAY': {
      if (event.playerId == null || event.substituteId == null) return snap;
      return {
        ...snap,
        awayLineup: snap.awayLineup.map((fp) =>
          fp.playerId === event.playerId ? { ...fp, playerId: event.substituteId! } : fp,
        ),
        awayBench: snap.awayBench.map((id) => (id === event.substituteId ? event.playerId! : id)),
      };
    }
    default:
      return snap;
  }
}

interface CallupData {
  homeShirts: Record<string, number>;
  awayShirts: Record<string, number>;
  homeLiberoId: number | null;
  awayLiberoId: number | null;
  ourTeamSide?: 'HOME' | 'AWAY';
  homeTeamName?: string;
  awayTeamName?: string;
  homeShirtColor?: string;
  awayShirtColor?: string;
}

export interface ReplayInfo {
  homeTeamName: string;
  awayTeamName: string;
  homeShirtColor: string;
  awayShirtColor: string;
}

export interface UseMatchReplayReturn {
  loading: boolean;
  error: string | null;
  // dati grezzi
  events: LiveGameEvent[];
  homeCallup: LivePlayer[];
  awayCallup: LivePlayer[];
  homeLiberoId: number | null;
  awayLiberoId: number | null;
  ourTeamSide: 'HOME' | 'AWAY';
  homeTeamName: string;
  awayTeamName: string;
  homeShirtColor: string;
  awayShirtColor: string;
  // navigazione
  cursor: number;
  total: number;
  sets: number[];
  // frame corrente
  currentSnapshot: GameStateSnapshot | null;
  currentEvent: LiveGameEvent | null;
  nextEvent: LiveGameEvent | null;
  // azioni
  goNext: () => void;
  goPrev: () => void;
  jumpToStart: () => void;
  jumpToEnd: () => void;
  jumpToSet: (setNumber: number) => void;
  jumpTo: (index: number) => void;
  reload: () => void;
}

/**
 * Gestisce la navigazione frame-per-frame di una partita registrata.
 *
 * Semantica del cursore:
 *   cursor = 0            → prima di qualsiasi evento (snapshot iniziale)
 *   cursor = k  (k > 0)  → k eventi applicati; snapshot = events[k] se esiste,
 *                           altrimenti stato finale ricostruito dall'ultimo evento
 *   cursor = events.length → fine partita
 *
 * currentEvent  = events[cursor - 1]  (evento appena avvenuto, null al frame 0)
 * currentSnapshot               = stato da visualizzare nel frame corrente
 */
export function useMatchReplay(eventId: number, liveMode = false): UseMatchReplayReturn {
  const [events, setEvents]           = useState<LiveGameEvent[]>([]);
  const [homeCallup, setHomeCallup]   = useState<LivePlayer[]>([]);
  const [awayCallup, setAwayCallup]   = useState<LivePlayer[]>([]);
  const [homeLiberoId, setHomeLiberoId] = useState<number | null>(null);
  const [awayLiberoId, setAwayLiberoId] = useState<number | null>(null);
  const [ourTeamSide, setOurTeamSide]   = useState<'HOME' | 'AWAY'>('HOME');
  const [homeTeamName, setHomeTeamName] = useState('Home');
  const [awayTeamName, setAwayTeamName] = useState('Away');
  const [homeShirtColor, setHomeShirtColor] = useState('#1565C0');
  const [awayShirtColor, setAwayShirtColor] = useState('#B71C1C');
  const [cursor, setCursor]           = useState(0);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [reloadKey, setReloadKey]     = useState(0);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    if (!liveMode) setCursor(0);

    Promise.all([
      liveMatchService.getEventLog(eventId),
      liveMatchService.getCallupData(eventId),
    ]).then(([evts, rawCallup]) => {
      if (cancelled) return;

      const evtList: LiveGameEvent[] = Array.isArray(evts) ? evts : [];
      setEvents(evtList);

      if (rawCallup) {
        const cd = rawCallup as CallupData;
        setHomeLiberoId(cd.homeLiberoId ?? null);
        setAwayLiberoId(cd.awayLiberoId ?? null);
        const side = cd.ourTeamSide ?? 'HOME';
        setOurTeamSide(side);
        if (cd.homeTeamName) setHomeTeamName(cd.homeTeamName);
        if (cd.awayTeamName) setAwayTeamName(cd.awayTeamName);
        if (cd.homeShirtColor) setHomeShirtColor(cd.homeShirtColor);
        if (cd.awayShirtColor) setAwayShirtColor(cd.awayShirtColor);

        const toPlayers = (shirts: Record<string, number>, isOurTeam: boolean): LivePlayer[] =>
          Object.entries(shirts ?? {}).map(([idStr, shirtNumber]) => ({
            id: Number(idStr),
            firstName: '',
            lastName: '',
            shirtNumber,
            gender: 'M',
            roles: [],
            isOurTeam,
          }));

        setHomeCallup(toPlayers(cd.homeShirts, side === 'HOME'));
        setAwayCallup(toPlayers(cd.awayShirts, side === 'AWAY'));
      }

      if (liveMode) setCursor(evtList.length);
      setLoading(false);
    }).catch(() => {
      if (!cancelled) {
        setError('Impossibile caricare il log della partita.');
        setLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [eventId, reloadKey]);

  // ─── Frame corrente ────────────────────────────────────────────────────────

  const currentSnapshot: GameStateSnapshot | null = (() => {
    if (events.length === 0) return null;
    if (cursor < events.length) return events[cursor].snapshot;
    // Cursore a fine log: applica l'ultimo evento al suo snapshot per ottenere lo stato post-evento completo
    const last = events[events.length - 1];
    return applyEventToSnapshot(last, last.snapshot);
  })();

  const currentEvent: LiveGameEvent | null = cursor > 0 ? events[cursor - 1] : null;
  const nextEvent: LiveGameEvent | null    = cursor < events.length ? events[cursor] : null;

  // Set presenti nel log, ordinati
  const sets = [...new Set(events.map((e) => e.setNumber))].sort((a, b) => a - b);

  // ─── Azioni di navigazione ─────────────────────────────────────────────────

  const goNext     = useCallback(() => setCursor((c) => Math.min(c + 1, events.length)), [events.length]);
  const goPrev     = useCallback(() => setCursor((c) => Math.max(c - 1, 0)), []);
  const jumpToStart = useCallback(() => setCursor(0), []);
  const jumpToEnd   = useCallback(() => setCursor(events.length), [events.length]);
  const jumpTo      = useCallback((index: number) => setCursor(Math.max(0, Math.min(index, events.length))), [events.length]);

  const jumpToSet = useCallback(
    (setNumber: number) => {
      const idx = events.findIndex((e) => e.setNumber === setNumber);
      setCursor(idx >= 0 ? idx : 0);
    },
    [events],
  );

  return {
    loading,
    error,
    events,
    homeCallup,
    awayCallup,
    homeLiberoId,
    awayLiberoId,
    ourTeamSide,
    homeTeamName,
    awayTeamName,
    homeShirtColor,
    awayShirtColor,
    cursor,
    total: events.length,
    sets,
    currentSnapshot,
    currentEvent,
    nextEvent,
    goNext,
    goPrev,
    jumpToStart,
    jumpToEnd,
    jumpToSet,
    jumpTo,
    reload,
  };
}
